import React from 'react';
import CopyButton from 'common/components/CopyButton';
import { Text } from 'common/styledComponents/typography';
import { highlight, Language } from 'modules/settings/components/DataExport/highlight';
import { splitInlineCode } from 'modules/settings/components/DataExport/utils';
import ModalV2 from 'common/components/Modal/ModalV2';
import {
  Code,
  CodeBlockContainer,
  GuideBullets,
  GuideHeading,
  GuideLink,
  GuideNote,
  GuideSection,
  GuideSteps,
  GuideTable,
  GuideTableRow,
  Pre,
} from 'modules/settings/components/DataExport/styled';

/** A translated sentence whose `backticked` parts render as inline code. */
export const GuideText: React.FC<{ children: string; fontWeight?: string }> = ({ children, fontWeight }) => (
  <Text variant='body2' color='black75' fontWeight={fontWeight}>
    {splitInlineCode(children).map(({ text, isCode }, index) => (isCode ? <Code key={index}>{text}</Code> : text))}
  </Text>
);

/** A copyable code sample. Samples never contain a real credential, only placeholders. */
interface CodeBlockProps {
  code: string;
  language: Language;
  /** Wrap long lines (curl commands) instead of scrolling sideways. */
  wrap?: boolean;
  /** What Copy puts on the clipboard, when it differs from what is shown (e.g. a body shown formatted). */
  copyValue?: string;
  'data-testid'?: string;
}

export const CodeBlock: React.FC<CodeBlockProps> = ({ code, language, wrap, copyValue, 'data-testid': dataTestId }) => (
  <CodeBlockContainer data-testid={dataTestId}>
    <Pre data-wrap={wrap}>
      <code>
        {highlight(code, language).map(({ text, type }, index) =>
          type === 'plain' ? (
            text
          ) : (
            <span key={index} data-token={type}>
              {text}
            </span>
          ),
        )}
      </code>
    </Pre>
    <CopyButton value={copyValue ?? code} />
  </CodeBlockContainer>
);

/** A link-style button on the card that opens a guide in a modal window. The guide mounts only while open. */
export const GuideModal: React.FC<{ id: string; title: string; children: React.ReactNode }> = ({ id, title, children }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <GuideLink type='button' aria-haspopup='dialog' onClick={() => setIsOpen(true)}>
        {title}
      </GuideLink>
      {isOpen && (
        <ModalV2
          isOpen
          appearance='card'
          headerStyle='page'
          inset={40}
          modalId={`${id}-modal`}
          title={title}
          customWidth={960}
          onClose={() => setIsOpen(false)}
        >
          {children}
        </ModalV2>
      )}
    </>
  );
};

export { GuideBullets, GuideHeading, GuideNote, GuideSection, GuideSteps, GuideTable, GuideTableRow };

/** Bullets of translated sentences. */
export const GuideBulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <GuideBullets>
    {items.map(item => (
      <li key={item}>
        <GuideText>{item}</GuideText>
      </li>
    ))}
  </GuideBullets>
);

/** Rows of a code (an event, a status) and what it means. */
export const GuideCodeTable: React.FC<{ rows: { code: string; text: string }[] }> = ({ rows }) => (
  <GuideTable>
    {rows.map(({ code, text }) => (
      <GuideTableRow key={code}>
        <div>
          <Code>{code}</Code>
        </div>
        <GuideText>{text}</GuideText>
      </GuideTableRow>
    ))}
  </GuideTable>
);
